/**
 * The FRONT-END presentation vocabulary for notification kinds. The wire carries an open `kind`
 * string (the frozen backend vocabulary — the client pattern-matches it, never enumerates it as a TS enum),
 * so this map turns each kind into a short category label for the notification row's meta line. Unknown
 * kinds fall back to a generic label (forward-compatible: a new backend kind renders sensibly before the
 * client ships its own copy). Purely presentational — no `delivery_class` reaches the client.
 *
 * There is no per-kind ICON any more (ART-79, 2026-08-28): the product owner's SME asked for the row
 * thumbnail gone, so the row is text-only and the label is the whole of what a kind contributes.
 */
const KIND_LABEL: Record<string, string> = {
  // Booking / schedule
  show_confirmed: "Show",
  logistics_added: "Logistics",
  dos_load_in: "Day of show",
  dos_showtime: "Day of show",
  support_added: "Show",
  flight_delay: "Travel",
  // The band finished the money on a date (ART-305 v2) → the AV tier, tour managers and management.
  show_settled: "Settlement",
  payments_closed_out: "Settlement",

  // Opportunities / offers / invites
  show_opportunity: "Opportunity",
  tour_opportunity: "Opportunity",
  rehearsal_invite: "Invite",
  content_invite: "Invite",
  pr_invite: "Invite",
  placement_invite: "Invite",
  opportunity_uninvited: "Opportunity",
  // An RSVP answers a show OR an opportunity, so it names neither (ART-228: a show response read "Opportunity").
  opportunity_response: "RSVP",
  // A show member backed out / declined (ART-305) — an RSVP too, told to whoever put them on the show.
  show_member_out: "RSVP",
  opportunity_all_confirmed: "Opportunity",
  offer: "Offer",

  // Tasks
  task_assigned: "Task",

  // Messaging.
  //
  // NONE of the three messaging kinds reach the centre any more (PO, 2026-09-12): the inbox owns messaging
  // — the envelope badges unread chats and the thread carries reaction chips — so a bell row as well was
  // one event on two surfaces. The server withholds all three from the centre and its counts
  // (`NotificationKinds.MessagingKinds`); `message_reaction` was the exception for a day and the PO removed
  // it too, accepting that a reaction is then counted by no badge (it shows only as its chip in the thread,
  // plus the push).
  //
  // **The entries STAY, and the kinds are not dead.** The rows are still written and still PUSHED — the
  // dispatcher gates the push on the insert — and a tapped push deep-links to the thread through
  // `deepLink.ts`. Deleting these labels would leave the generic fallback on any surface that renders a
  // pushed notification, and would silently degrade the centre if the exclusion were ever relaxed.
  message_direct: "Message",
  message_band_thread: "Message",
  message_received: "Message",
  message_reaction: "Reaction",

  // People / collaboration
  friend_request: "Friend",
  team_removed: "Team",
  seat_invite: "Invite",
  partner: "Guest list",

  // Roster (org ↔ band)
  roster_invite: "Roster",
  roster_invite_accepted: "Roster",
  roster_invite_declined: "Roster",
  roster_access_revoked: "Roster",
  roster_artist_disconnected: "Roster",

  // Marketing
  post_spike: "Socials",

  // Admin / fallback
  generic: "Update",
};

const FALLBACK_LABEL = "Notification";

/** The short category label for a notification kind; a generic fallback for an unknown/absent kind. */
export function notificationKindLabel(kind: string | null | undefined): string {
  return (kind && KIND_LABEL[kind]) || FALLBACK_LABEL;
}
